<template>
  <div class="client-insights-root">
  <KvScrollbar class="insights">

    <div v-if="loading" class="d-flex flex-column gap-lg">

      <KvCard content-class="kv-ai-chat-limit">
        <template #text>
          <div class="d-flex flex-column h-100 justify-content-center align-items-center">

            <KvSkeleton width="24px" height="24px" style="border-radius: 50%; margin-bottom: 16px;" />

            <KvSkeleton width="220px" height="24px" style="margin-bottom: 12px;" />
            <KvSkeleton width="340px" height="20px" />

          </div>

          <div class="d-flex gap-sm mt-auto w-100 pt-4">
            <KvSkeleton class="flex-1" height="42px" />
            <KvSkeleton width="110px" height="42px" />
          </div>
        </template>
      </KvCard>

      <div class="d-flex flex-column gap-sm">
        <div class="d-flex justify-content-between align-items-center">
          <KvSkeleton width="150px" height="24px" />
          <KvSkeleton width="120px" height="36px" />
        </div>
        <div class="d-flex gap-sm">
          <KvSkeleton class="flex-1" height="80px" />
          <KvSkeleton class="flex-1" height="80px" />
        </div>
        <div class="d-flex gap-sm">
          <KvSkeleton class="flex-1" height="80px" />
          <KvSkeleton class="flex-1" height="80px" />
        </div>
        <div class="d-flex gap-sm">
          <KvSkeleton class="flex-1" height="120px" />
          <KvSkeleton class="flex-1" height="120px" />
        </div>
      </div>

      <div class="d-flex flex-column gap-sm">
        <div class="d-flex justify-content-between align-items-center">
          <KvSkeleton width="150px" height="24px" />
          <KvSkeleton width="100px" height="36px" />
        </div>
        <KvCard variant="soft">
          <template #text>
            <KvSkeleton :rows="3" />
          </template>
        </KvCard>
      </div>

      <div class="d-flex flex-column gap-sm">
        <KvSkeleton width="150px" height="24px" />
        <div class="notifications gap-sm">
          <KvSkeleton height="200px" width="100%" />
          <KvSkeleton height="200px" width="100%" />
          <KvSkeleton height="200px" width="100%" />
        </div>
      </div>

      <div class="d-flex flex-column gap-sm">
        <div class="d-flex justify-content-between align-items-center">
          <KvSkeleton width="100px" height="24px" />
          <div class="d-flex gap-sm">
            <KvSkeleton width="100px" height="36px" />
            <KvSkeleton width="150px" height="36px" />
          </div>
        </div>
        <KvCard variant="soft">
          <template #text>
            <div class="d-flex gap-sm mb-md">
              <KvSkeleton class="flex-1" height="100px" />
              <div class="d-flex flex-column flex-1 gap-sm">
                <KvSkeleton class="flex-1" height="46px" />
                <KvSkeleton class="flex-1" height="46px" />
              </div>
            </div>
            <KvSkeleton :rows="4" />
          </template>
        </KvCard>
      </div>

    </div>

    <template v-else-if="insights">
      <KvCard :content-class="['kv-ai-chat-limit', { 'kv-ai-chat-expanded': hasMessages }]">
        <template #text>
          <KvScrollbar ref="aiScrollbar" content-class="kv-ai-chat">
            <div v-if="insights.messages && insights.messages.length > 0" class="kv-ai-messages">
              <span v-for="(message, index) in insights.messages" :key="index" class="chat-message" :id="message.id" :class="[{ 'ai-message': message.aiMessage, 'user-message': !message.aiMessage }]">
                <div class="message-body" :class="{ 'message-loading': message.loading }">
                  <template v-if="!message.loading">
                    <span class="content" v-html="message.content"></span>
                    <span class="date">{{ getTime(message.date) }}</span>
                  </template>
                  <template v-else>
                    <div class="dots-loader"></div>
                  </template>
                </div>
              </span>
            </div>
            <div v-else class="kv-ai-placeholder">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.9682 11.0908L16.2598 9.67395C16.0109 9.19528 15.896 8.94637 15.7428 8.7549C15.6088 8.56343 15.4365 8.41025 15.245 8.25708C15.0344 8.1039 14.8046 7.96987 14.326 7.74011L12.9091 7.03168L14.326 6.32324C14.8046 6.07433 15.0535 5.95945 15.245 5.80627C15.4365 5.67225 15.5897 5.49992 15.7428 5.30846C15.896 5.09784 16.03 4.86808 16.2598 4.3894L16.9682 2.97253L17.6767 4.3894C17.9256 4.86808 18.0405 5.11699 18.1936 5.30846C18.3277 5.49992 18.5 5.6531 18.6915 5.80627C18.9021 5.95945 19.1318 6.09348 19.6105 6.32324L21.0274 7.03168L19.6105 7.74011C19.1318 7.98902 18.8829 8.1039 18.6915 8.25708C18.5 8.39111 18.3468 8.56343 18.1936 8.7549C18.0405 8.96551 17.9064 9.19528 17.6767 9.67395L16.9682 11.0908Z" fill="url(#paint0_linear_5110_13207)" />
                <path d="M9.29089 21.028L8.21866 18.2517C7.96975 17.5815 7.83572 17.2561 7.64425 16.988C7.47193 16.7391 7.26131 16.5285 7.0124 16.3561C6.7252 16.1647 6.3997 16.0306 5.74871 15.7817L2.97241 14.7095L5.74871 13.6373C6.41885 13.3884 6.74435 13.2543 7.0124 13.0629C7.26131 12.8906 7.47193 12.6799 7.64425 12.431C7.83572 12.1438 7.96975 11.8183 8.21866 11.1673L9.29089 8.39104L10.3631 11.1673C10.612 11.8375 10.7461 12.163 10.9375 12.431C11.1098 12.6799 11.3205 12.8906 11.5694 13.0629C11.8566 13.2543 12.1821 13.3884 12.8331 13.6373L15.6094 14.7095L12.8331 15.7817C12.1629 16.0306 11.8374 16.1647 11.5694 16.3561C11.3205 16.5285 11.1098 16.7391 10.9375 16.988C10.7461 17.2752 10.612 17.6007 10.3631 18.2517L9.29089 21.028Z" fill="url(#paint1_linear_5110_13207)" />
                <defs>
                  <linearGradient id="paint0_linear_5110_13207" x1="6.43507" y1="30.6135" x2="26.868" y2="24.5047" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#F49E8A" />
                    <stop offset="0.3" stop-color="#E83E76" />
                    <stop offset="0.65" stop-color="#936CAD" />
                    <stop offset="1" stop-color="#2DBBDD" />
                  </linearGradient>
                  <linearGradient id="paint1_linear_5110_13207" x1="6.43507" y1="30.6135" x2="26.868" y2="24.5047" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#F49E8A" />
                    <stop offset="0.3" stop-color="#E83E76" />
                    <stop offset="0.65" stop-color="#936CAD" />
                    <stop offset="1" stop-color="#2DBBDD" />
                  </linearGradient>
                </defs>
              </svg>

              <KvTypography color="ai" weight="medium" size="medium">{{ t('clients-insights-ai_title') }}</KvTypography>
              <KvTypography size="small" color="muted">{{ t('clients-insights-ai_subtitle') }}</KvTypography>
            </div>

          </KvScrollbar>
          <div class="kv-ai-chat-actions d-flex gap-sm">
            <KvInput size="large" class="ai-message" variant="outlined" v-model="question" :placeholder="t('clients-insights-type_message')" @keydown.enter.prevent="handleSendMessage" />

            <KvButton :disabled="insights.messages.some(x => x.loading)" variant="ai" size="large" @click="handleSendMessage">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5036 8.99261L14.7188 7.42315C14.4431 6.89292 14.3158 6.6172 14.1462 6.40512C13.9977 6.19303 13.8068 6.02335 13.5947 5.85368C13.3614 5.68401 13.1069 5.53555 12.5767 5.28104L11.0071 4.49631L12.5767 3.71157C13.1069 3.43586 13.3826 3.3086 13.5947 3.13893C13.8068 2.99047 13.9765 2.79959 14.1462 2.5875C14.3158 2.3542 14.4643 2.09969 14.7188 1.56947L15.5036 0L16.2883 1.56947C16.5641 2.09969 16.6913 2.37541 16.861 2.5875C17.0095 2.79959 17.2003 2.96926 17.4124 3.13893C17.6457 3.3086 17.9002 3.45707 18.4305 3.71157L20 4.49631L18.4305 5.28104C17.9002 5.55676 17.6245 5.68401 17.4124 5.85368C17.2003 6.00214 17.0307 6.19303 16.861 6.40512C16.6913 6.63841 16.5428 6.89292 16.2883 7.42315L15.5036 8.99261Z" fill="#FCFCFD" />
                <path d="M6.99916 20L5.81142 16.9247C5.5357 16.1824 5.38723 15.8218 5.17513 15.5249C4.98425 15.2492 4.75094 15.0159 4.47522 14.825C4.15707 14.6129 3.79651 14.4645 3.07538 14.1887L0 13.001L3.07538 11.8133C3.81772 11.5376 4.17828 11.3892 4.47522 11.1771C4.75094 10.9862 4.98425 10.7529 5.17513 10.4772C5.38723 10.159 5.5357 9.79848 5.81142 9.07737L6.99916 6.00207L8.18689 9.07737C8.46262 9.81969 8.61108 10.1802 8.82318 10.4772C9.01407 10.7529 9.24737 10.9862 9.52309 11.1771C9.84124 11.3892 10.2018 11.5376 10.9229 11.8133L13.9983 13.001L10.9229 14.1887C10.1806 14.4645 9.82003 14.6129 9.52309 14.825C9.24737 15.0159 9.01407 15.2492 8.82318 15.5249C8.61108 15.843 8.46262 16.2036 8.18689 16.9247L6.99916 20Z" fill="#FCFCFD" />
              </svg>
              {{ t('clients-insights-send') }}
            </KvButton>
          </div>
        </template>
      </KvCard>

      <KvExpandable default-open>
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-purchases') }}</KvTypography>
          </div>

        </template>

        <template #actions>
          <KvButton variant="outlined" @click="$emit('open-order-modal')">{{ t('clients-basic-include_orders') }}</KvButton>
        </template>

        <template #content>
          <div class="d-flex flex-column gap-sm">

            <div class="d-flex gap-sm">
              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <KvTypography color="muted">{{ t('clients-insights-first_purchase') }}</KvTypography>
                </template>
                <template #text>
                  <div class="d-flex flex-column">
                    <KvTypography weight="semibold">
                      <HumanDate v-if="purchases_report.first_purchase" :date="purchases_report.first_purchase.date" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>
                    </KvTypography>
                    <KvTypography color="muted" size="small" weight="medium">
                      {{ purchases_report.first_purchase ? purchases_report.first_purchase.branch_name : '' }}
                    </KvTypography>
                  </div>
                </template>
              </KvCard>

              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <KvTypography weight="medium" color="muted">{{ t('clients-insights-last_purchase') }}</KvTypography>
                </template>
                <template #text>
                  <div class="d-flex flex-column">
                    <KvTypography weight="semibold">
                      <HumanDate v-if="purchases_report.last_purchase" :date="purchases_report.last_purchase.date" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>
                    </KvTypography>
                    <KvTypography color="muted" size="small" weight="medium">
                      {{ purchases_report.last_purchase ? purchases_report.last_purchase.branch_name : '' }}
                    </KvTypography>
                  </div>
                </template>
              </KvCard>
            </div>
            <div class="d-flex gap-sm">
              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <KvTypography weight="medium" color="muted">{{ t('clients-detail-next_order_prediction') }}</KvTypography>
                </template>
                <template #text>
                  <div v-if="purchases_report.next_order_prediction" class="d-flex justify-content-between align-items-center flex-wrap gap-sm">
                    <KvTypography weight="semibold">
                      <HumanDate :date="purchases_report.next_order_prediction.prediction_date" format="[d]/[M]/[YY]" />
                    </KvTypography>
                    <KvChip :color="getPredictionState(purchases_report.next_order_prediction.prediction_date).color" rounded="soft" density="compact">
                      {{ getPredictionState(purchases_report.next_order_prediction.prediction_date).label }}
                    </KvChip>
                  </div>
                  <div v-else>
                    <KvTypography weight="semibold">-</KvTypography>
                  </div>
                </template>
              </KvCard>
              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <KvTypography weight="medium" color="muted">{{ t('clients-insights-most_purchased_item') }}</KvTypography>
                </template>
                <template #text>
                  <div class="d-flex flex-column">
                    <KvTypography truncate :title="purchases_report.most_purchased_item?.name ?? t('clients-not_informed')" weight="semibold">{{ purchases_report.most_purchased_item?.name ?? t('clients-not_informed') }}</KvTypography>
                  </div>
                </template>
              </KvCard>
            </div>
            <div class="d-flex gap-sm">
              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-sm">
                    <KvTypography weight="medium" color="muted">{{ t('clients-insights-cumulative_spend') }}</KvTypography>
                    <KvSelect :options="periods" v-model="period.purchases" size="small" @change="(value) => changePeriod(value, loadPurchasesReport)" />
                  </div>
                </template>
                <template #text>
                  <div class="d-flex flex-column gap-md">
                    <KvTypography weight="semibold" size="large">
                      {{ currencySymbol }}{{ formatNumber(purchases_report.cumulative_spend.total_value) }}
                    </KvTypography>
                    <SmoothLineChart v-if="cumulative_spend_chart.datasets.length > 1" :chart-data="cumulative_spend_chart" :height="50" />
                  </div>

                </template>
              </KvCard>
              <KvCard density="compact" variant="soft" class="flex-1">
                <template #title>
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-sm">
                    <KvTypography weight="medium" color="muted">{{ t('clients-insights-total_purchases') }}</KvTypography>
                    <KvSelect :options="periods" v-model="period.purchases" size="small" @change="(value) => changePeriod(value, loadPurchasesReport)" />
                  </div>
                </template>
                <template #text>
                  <div class="d-flex flex-column gap-md">
                    <KvTypography weight="semibold" size="large">
                      {{ purchases_report.total_purchases.total_quantity }}
                    </KvTypography>
                    <SmoothLineChart v-if="total_purchases_chart.datasets.length > 1" :chart-data="total_purchases_chart" :height="50" />
                  </div>

                </template>
              </KvCard>
            </div>
          </div>
        </template>
      </KvExpandable>

      <KvExpandable default-open v-if="predictions_report && predictions_report.length > 0">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-replenishment') }}</KvTypography>
          </div>
        </template>
        <template #actions>
          <KvButton variant="outlined" @click="togglePreview('predictions', loadPredictionsReport)">{{ preview.predictions === 'last' ? t('clients-insights-view_all') : t('clients-insights-view_latest') }}</KvButton>
        </template>

        <template #content>
          <KvCard variant="soft" density="compact">
            <template #text>
              <div class="d-flex flex-column gap-sm">
                <KvTypography v-if="predictions_report.some(x => x.has_stock)" weight="semibold" color="muted">{{ t('clients-insights-with_stock') }}</KvTypography>
                <div v-for="prediction in predictions_report.filter(x => x.has_stock)" class="d-flex justify-content-between align-items-center">
                  <KvTypography color="muted"><!-- i18n-validator-ignore-next-line: runtime product group name -->{{ prediction.group_name }}</KvTypography>
                  <div class="d-flex gap-sm align-items-center">
                    <KvChip density="compact" :color="getPredictionState(prediction.predicted_next_purchase_date).color">{{ getPredictionState(prediction.predicted_next_purchase_date).label }}</KvChip>
                    <KvTypography color="muted">
                      <HumanDate :date="prediction.predicted_next_purchase_date" format="[d]/[M]/[YY]"></HumanDate>
                    </KvTypography>
                  </div>
                </div>

                <div class="line mt-1 mb-1" v-if="predictions_report.some(x => x.has_stock) && predictions_report.some(x => !x.has_stock)"></div>
                <KvTypography v-if="predictions_report.some(x => !x.has_stock)" weight="semibold" color="muted">{{ t('clients-insights-without_stock') }}</KvTypography>
                <div v-for="prediction in predictions_report.filter(x => !x.has_stock)" class="d-flex justify-content-between align-items-center">
                  <KvTypography color="muted"><!-- i18n-validator-ignore-next-line: runtime product group name -->{{ prediction.group_name }}</KvTypography>
                  <div class="d-flex gap-sm align-items-center">
                    <KvChip density="compact" :color="getPredictionState(prediction.predicted_next_purchase_date).color">{{ getPredictionState(prediction.predicted_next_purchase_date).label }}</KvChip>
                    <KvTypography color="muted">
                      <HumanDate :date="prediction.predicted_next_purchase_date" format="[d]/[M]/[YY]"></HumanDate>
                    </KvTypography>
                  </div>

                </div>
              </div>
            </template>
          </KvCard>
        </template>
      </KvExpandable>

      <KvExpandable default-open>
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-communications') }}</KvTypography>
          </div>
        </template>
        <template #content>
          <div class="gap-sm notifications">
            <KvCard variant="soft" density="compact">
              <template #title>
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-sm">
                  <KvTypography color="muted">{{ t('clients-insights-contacts') }}</KvTypography>
                  <KvSelect :options="periods" v-model="period.notifications" size="small" @change="(value) => changePeriod(value, loadNotificationsReport)" />
                </div>
              </template>
              <template #text>
                <DoughnutChart :thickness="8" :height="150" :chartData="notificationsChartData" />
              </template>
            </KvCard>
            <KvCard variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">{{ t('clients-insights-last_contact_date') }}</KvTypography>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-sm">
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg email"> {{ t('clients-columns-email') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.email.last_contact" :date="notifications_report.milestones.email.last_contact" format="[d]/[M]/[YY]" />
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg sms">{{ t('clients-detail-sms') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.sms.last_contact" :date="notifications_report.milestones.sms.last_contact" format="[d]/[M]/[YY]" />
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg whatsapp">{{ t('clients-detail-whatsapp') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.whatsapp.last_contact" :date="notifications_report.milestones.whatsapp.last_contact" format="[d]/[M]/[YY]" />
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg rcs">{{ t('rcs') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.rcs && notifications_report.milestones.rcs.last_contact" :date="notifications_report.milestones.rcs.last_contact" format="[d]/[M]/[YY]" />
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                </div>
              </template>
            </KvCard>
            <KvCard variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">{{ t('clients-insights-first_contact_date') }}</KvTypography>
              </template>
              <template #text>
                <div class="d-flex flex-column gap-sm">
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg email">{{ t('clients-columns-email') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.email.first_contact" :date="notifications_report.milestones.email.first_contact" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg sms">{{ t('clients-detail-sms') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.sms.first_contact" :date="notifications_report.milestones.sms.first_contact" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>

                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg whatsapp">{{ t('clients-detail-whatsapp') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.whatsapp.first_contact" :date="notifications_report.milestones.whatsapp.first_contact" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                  <div class="d-flex flex-column">
                    <KvTypography weight="medium" size="small" color="muted" class="circle-bg rcs">{{ t('rcs') }}</KvTypography>
                    <KvTypography weight="medium" size="small" color="primary">
                      <HumanDate v-if="notifications_report.milestones.rcs && notifications_report.milestones.rcs.first_contact" :date="notifications_report.milestones.rcs.first_contact" format="[d]/[M]/[YY]"></HumanDate>
                      <span v-else>-</span>
                    </KvTypography>
                  </div>
                </div>
              </template>
            </KvCard>

          </div>
        </template>
      </KvExpandable>

      <KvExpandable v-if="$pointsProgram()" default-open class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-points') }}</KvTypography>
          </div>

        </template>

        <template #actions>
          <div class="d-flex gap-sm flex-wrap justify-content-end">
            <KvButton variant="outlined" @click="togglePreview('rewards', loadRewardsReport)">{{ preview.rewards === 'last' ? t('clients-insights-view_all') : t('clients-insights-view_latest') }}</KvButton>

            <KvButton variant="outlined" @click="$emit('open-points-modal')">{{ t('clients-modals-manage_points') }}</KvButton>

          </div>

        </template>

        <template #content>

          <KvCard density="compact" variant="soft">
            <template #text>
              <div class="d-flex gap-sm">
                <KvCard variant="tonal" class="flex-1">
                  <template #title>
                    <KvTypography color="muted">{{ t('clients-insights-available_points') }}</KvTypography>
                  </template>
                  <template #text>
                    <KvTypography weight="medium" size="giant" color="success">{{ rewards_report.available_points }}</KvTypography>
                  </template>
                </KvCard>
                <div class="d-flex flex-column flex-1 gap-sm">
                  <KvCard density="compact" variant="tonal" class="flex-1">
                    <template #text>
                      <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-insights-total_accumulated') }}</KvTypography>
                        <KvTypography size="large" color="primary" weight="semibold">{{ rewards_report.total_accumulated }}</KvTypography>
                      </div>
                    </template>
                  </KvCard>
                  <KvCard density="compact" variant="tonal" class="flex-1">
                    <template #text>
                      <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-insights-points_used') }}</KvTypography>
                        <KvTypography size="large" color="primary" weight="semibold">{{ rewards_report.total_used }}</KvTypography>
                      </div>
                    </template>
                  </KvCard>
                </div>
              </div>
              <KvScrollbar content-class="rewards-container" v-if="rewards_report.last_transactions.length > 0">
                <table class="rewards-table">
                  <thead>
                    <tr>
                      <th>
                        <KvTypography color="muted" size="small" weight="medium">{{ t('clients-insights-reward') }}</KvTypography>
                      </th>
                      <th>
                        <KvTypography color="muted" size="small" weight="medium">{{ t('clients-insights-points') }}</KvTypography>
                      </th>
                    </tr>

                  </thead>

                  <tbody>
                    <tr v-for="transaction in rewards_report.last_transactions">
                      <td>
                        <KvTypography color="secondary">
                          {{ parseTransactionDescription(transaction.description) }}
                        </KvTypography>
                      </td>

                      <td>
                        <KvTypography :color="transaction.amount < 0 ? 'error' : 'success'">{{ transaction.amount }}
                        </KvTypography>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </KvScrollbar>
            </template>
          </KvCard>


        </template>
      </KvExpandable>

      <KvExpandable default-open class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-forms') }}</KvTypography>
          </div>
        </template>

        <template #actions>
          <div class="d-flex gap-sm flex-wrap justify-content-end">
            <KvButton v-if="forms_report.answers.length > 0" variant="outlined" @click="togglePreview('forms', loadFormsReport)">{{ preview.forms === 'last' ? t('clients-insights-view_all') : t('clients-insights-view_latest') }}</KvButton>

            <KvButton variant="outlined" @click="$emit('open-forms-modal')">{{ t('clients-insights-create_unique_link') }}</KvButton>
          </div>

        </template>

        <template #content>
          <KvCard density="compact" variant="soft">
            <template #title>
              <KvTypography color="muted">{{ t('clients-insights-latest_forms') }}</KvTypography>
            </template>
            <template #text>

              <div v-if="forms_report.answers.length > 0" class="client-answers-list">
                <div
                  class="client-answer-row"
                  v-for="answer in forms_report.answers"
                  :key="answer.answer_id"
                  :class="{ selected: String(activeMessage) === String(answer.answer_id) }"
                  @click="openForm(answer.answer_id)"
                >
                  <div class="form-cell">
                    <span class="form-name">
                      <!-- i18n-validator-ignore-next-line: runtime form name -->{{ answer.form_name }}
                    </span>
                    <div
                      class="type-summary"
                      v-if="visibleTypeSummary(answer).length"
                    >
                      <kv-tooltip
                        v-for="(item, index) in visibleTypeSummary(answer)"
                        :key="`${item.type}-${index}`"
                        :text="typeSummaryTitle(item)"
                        placement="top"
                      >
                        <span
                          class="type-item"
                          :class="typeToneClass(item)"
                        >
                          <span :class="getIconForSummary(item)"></span>
                        </span>
                      </kv-tooltip>
                    </div>
                    <div class="message-tags-cell" @click.stop>
                      <answer-tags
                        compact
                        display="dots"
                        :max-dots="999"
                        :tags="answer.tags || []"
                        :suggestions="suggestionsFor(answer)"
                        :busy="tagBusyId === answer.answer_id"
                        @apply="applyListTag(answer, $event)"
                        @remove="removeListTag(answer, $event)"
                        @update="updateTagDefinition(answer, $event)"
                        @delete="deleteTagDefinition(answer, $event)"
                      />
                    </div>
                  </div>
                  <human-date
                    class="date"
                    v-if="answer.answered_date"
                    :date="answer.answered_date"
                    :format="t('dates-format_date_time')"
                  />
                </div>
              </div>

              <div v-else class="d-flex flex-column align-items-center justify-content-center py-1 text-center gap-sm">

                <Icon icon-class="ui-file-05" size="12" />

                <div class="d-flex flex-column align-items-center gap-xs">
                  <KvTypography weight="medium" color="secondary">{{ t('clients-insights-no_forms_sent') }}</KvTypography>
                  <KvTypography size="small" color="muted" style="max-width: 280px;">
                    {{ t('clients-insights-no_forms_sent_subtitle') }}
                  </KvTypography>
                </div>
              </div>

            </template>
          </KvCard>
        </template>
      </KvExpandable>

      <KvExpandable default-open class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-tasks') }}</KvTypography>
          </div>
        </template>

        <template #actions>
          <div class="d-flex gap-sm">
            <KvButton v-if="tasks_report.last_tasks.length > 0" variant="outlined" @click="togglePreview('tasks', loadTasksReport)">{{ preview.tasks === 'last' ? t('clients-insights-view_all') : t('clients-insights-view_latest') }}</KvButton>

            <KvButton variant="outlined" @click="$emit('open-tasks-modal')">{{ t('clients-basic-create_task') }}</KvButton>

          </div>

        </template>

        <template #content>
          <div class="grid-items gap-sm">
            <KvCard variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-generated') }}
                </KvTypography>

              </template>
              <template #text>
                <KvTypography weight="semibold" size="large">
                  {{ tasks_report.total_generated }}
                </KvTypography>
              </template>
            </KvCard>
            <KvCard variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-completed') }}
                </KvTypography>
              </template>
              <template #text>
                <div class="d-flex justify-content-between align-items-center">
                  <KvTypography weight="semibold" size="large">
                    {{ tasks_report.total_completed }}
                  </KvTypography>
                  <div class="d-flex flex-column align-items-end">
                    <KvTypography size="small" weight="medium" color="muted">
                      {{ t('clients-insights-conversion') }}
                    </KvTypography>
                    <div class="d-flex align-items-center gap-sm">
                      <KvTypography size="small" weight="medium" color="secondary">
                        {{ tasks_report.completion_rate_percent }}%
                      </KvTypography>
                      <DoughnutChart :thickness="3.5" :height="16" :width="16" :gap="0" :show-labels="false" :chartData="conversionChart({ total: tasks_report.total_generated, converted: tasks_report.total_completed })" />
                    </div>
                  </div>
                </div>
              </template>
            </KvCard>
            <KvCard variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-completed_on_time') }}
                </KvTypography>
              </template>
              <template #text>
                <div class="d-flex justify-content-between align-items-center">
                  <KvTypography weight="semibold" size="large">
                    {{ tasks_report.total_completed_on_time }}
                  </KvTypography>
                  <div class="d-flex flex-column align-items-end">
                    <KvTypography size="small" weight="medium" color="muted">
                      {{ t('clients-insights-conversion') }}
                    </KvTypography>

                    <div class="d-flex align-items-center gap-sm">
                      <KvTypography size="small" weight="medium" color="secondary">
                        {{ tasks_report.on_time_rate_percent }}%
                      </KvTypography>
                      <DoughnutChart :thickness="3.5" :height="16" :width="16" :gap="0" :show-labels="false" :chartData="conversionChart({ total: tasks_report.total_completed, converted: tasks_report.total_completed_on_time })" />
                    </div>
                  </div>
                </div>

              </template>
            </KvCard>
          </div>
          <KvCard density="compact" variant="soft" class="mt-2" v-if="tasks_report.last_tasks.length > 0">
            <template #title>
              <KvTypography color="muted">{{ t('clients-insights-latest_tasks') }}</KvTypography>
            </template>
            <template #text>

              <div class="d-flex flex-column gap-sm">
                <KvCard v-for="task in tasks_report.last_tasks" :key="task.id" density="compact" :variant="task.completed_at ? 'tonal' : 'default'" class="form-item">
                  <template #text>
                    <div class="d-flex justify-content-between align-items-center mobile-column-layout">
                      <KvTypography color="muted" truncate class="flex-1">
                        {{ task.title }}
                      </KvTypography>
                      <div class="d-flex align-items-center gap-sm mobile-actions-row">
                        <KvButton class="show-on-hover" size="small" variant="outlined" prepend-icon="ui-eye" :to="{
                          path: '/tasks',
                          query: {
                            task_id: task.id,
                            filter: JSON.stringify({
                              status: task.completed_at ? 'completed' : 'pending',
                              'task-created-date-period': {
                                start: task.created_at, 
                                end: task.created_at   
                              },
                              assignee: ''
                            })
                          }
                        }">
                          {{ t('clients-insights-view') }}
                        </KvButton>
                        <KvChip :color="task.completed_at ? 'success' : 'warning'" density="compact" rounded="medium">{{ task.completed_at ? t('clients-insights-completed_single') : t('clients-insights-pending_single') }}</KvChip>
                      </div>

                    </div>
                  </template>
                </KvCard>

              </div>
            </template>
          </KvCard>


        </template>
      </KvExpandable>

      <KvExpandable default-open class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center">
            <KvTypography size="medium" weight="medium" color="secondary">{{ t('clients-insights-kevi_coupons') }}</KvTypography>
          </div>
        </template>

        <template #actions>
          <div class="d-flex gap-sm">
            <KvButton v-if="coupons_report.last_generated_coupons.length > 0" variant="outlined" @click="togglePreview('coupons', loadCouponsReport)">{{ preview.coupons === 'last' ? t('clients-insights-view_all') : t('clients-insights-view_latest') }}</KvButton>

          </div>

        </template>

        <template #content>
          <div class="d-flex flex-wrap gap-sm">
            <KvCard class="flex-1" variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-active_coupons') }}
                </KvTypography>

              </template>
              <template #text>
                <KvTypography weight="semibold" size="large">
                  {{ coupons_report.total_active }}
                </KvTypography>
              </template>
            </KvCard>
            <KvCard class="flex-1" variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-used_coupons') }}
                </KvTypography>

              </template>
              <template #text>
                <KvTypography weight="semibold" size="large">
                  {{ coupons_report.total_used }}
                </KvTypography>
              </template>
            </KvCard>
            <KvCard class="flex-1" variant="soft" density="compact">
              <template #title>
                <KvTypography color="muted">
                  {{ t('clients-insights-expired_coupons') }}
                </KvTypography>

              </template>
              <template #text>
                <KvTypography weight="semibold" size="large">
                  {{ coupons_report.total_expired }}
                </KvTypography>
              </template>
            </KvCard>
          </div>
          <KvCard density="compact" variant="soft" class="mt-2" v-if="coupons_report.last_generated_coupons.length > 0">
            <template #title>
              <KvTypography color="muted">{{ t('clients-insights-latest_coupons') }}</KvTypography>
            </template>
            <template #text>

              <div class="d-flex flex-column gap-sm">
                <KvCard v-for="coupon in coupons_report.last_generated_coupons" :key="coupon.id" density="compact" :variant="getCouponStatus(coupon).variant" class="coupon-item">
                  <template #text>
                    <div class="d-flex justify-content-between align-items-center coupon-layout">

                      <div class="d-flex flex-column gap-sm coupon-info">
                        <KvTypography color="muted" weight="semibold">
                          {{ coupon.description }}
                        </KvTypography>
                        <KvTypography color="muted" weight="medium" size="small">
                          {{ t('valid_between') }}:
                          <HumanDate :date="coupon.valid_from" format="[d]/[M]/[YY]" /> a
                          <HumanDate :date="coupon.expiration_date" format="[d]/[M]/[YY]" />
                        </KvTypography>
                      </div>

                      <div class="d-flex align-items-center gap-md coupon-actions">
                        <KvChip density="compact" rounded="medium" :color="getCouponStatus(coupon).color">{{ getCouponStatus(coupon).label }}</KvChip>
                        <KvCopyButton :value="coupon.code" />
                      </div>

                    </div>
                  </template>
                </KvCard>
              </div>
            </template>
          </KvCard>


        </template>
      </KvExpandable>
    </template>

  </KvScrollbar>

  <drawer
    ref="messageDrawer"
    id="client-message-drawer"
    size="xl"
    :has-header="false"
    :backdrop="false"
    @hide="handleDrawerHide"
  >
    <template #body="{ close }">
      <message
        :id="activeMessage"
        :place-tags="placeTags"
        :hide-client-section="true"
        @close="close"
        @tags-changed="onDrawerTagsChanged"
        @duplicated="onAnswerDuplicated"
      />
    </template>
  </drawer>
  </div>

</template>


<script>
import KvInput from "@/components/inputs/kv-input.vue"
import KvButton from "@/components/form/kv-button.vue"
import KvCopyButton from "@/components/form/kv-copy-button.vue"
import KvTypography from "@/components/ui/kv-typography.vue"
import KvCard from "@/components/ui/kv-card.vue";
import KvScrollbar from "@/components/ui/kv-scrollbar.vue";
import KvChip from "@/components/ui/kv-chip.vue";
import KvExpandable from "@/components/ui/kv-expandable.vue";
import HumanDate from "@/components/human-date/human-date.vue";
import SmoothLineChart from "@/components/charts/smooth-line-chart.vue";
import KvActionSwitch from "./kv-action-switch.vue"
import DoughnutChart from "@/components/charts/doughnut-chart.vue";
import KvSelect from "@/components/inputs/kv-select.vue";
import KvSkeleton from "@/components/ui/kv-skeleton.vue";
import Icon from "@/components/icon/icon.vue";
import Drawer from "@/components/drawer/drawer.vue";
import Message from "@/inbox/_message.vue";
import AnswerTags from "@/inbox/answer-tags.vue";
import KvTooltip from "@/components/ui/kv-tooltip.vue";
import keviApi from "@/keviApi";
import { mapActions } from "vuex";

export default {
  props: {
    client: Object,
    loading: { type: Boolean, default: false },
    insights: Object,
    sendAiMessage: Function,
    loadRewardsReport: Function,
    loadFormsReport: Function,
    loadTasksReport: Function,
    loadCouponsReport: Function,
    loadNotificationsReport: Function,
    loadPurchasesReport: Function,
    loadPredictionsReport: Function,
    setPointsModalRef: {
      type: Object,
      default: null,
    },
  },
  components: {
    KvInput,
    KvButton,
    KvTypography,
    KvCard,
    KvScrollbar,
    KvChip,
    KvExpandable,
    HumanDate,
    SmoothLineChart,
    KvActionSwitch,
    KvCopyButton,
    DoughnutChart,
    KvSelect,
    KvSkeleton,
    Icon,
    Drawer,
    Message,
    AnswerTags,
    KvTooltip,
  },
  data: function () {
    return {
      question: "",
      preview: { rewards: "last", forms: "last", tasks: "last", coupons: "last", predictions: "last" },
      changePoint: {
        amount: 1,
        active: true,
      },
      processingPoints: false,
      periods: [
        { label: '30 dias', value: '30_days' },
        { label: '90 dias', value: '90_days' },
        { label: '180 dias', value: '180_days' },
        { label: '1 ano', value: '1_year' },
        { label: 'Todo período', value: 'all_time' }
      ],
      period: {
        notifications: 'all_time',
        purchases: "all_time"
      },
      activeMessage: null,
      placeTags: [],
      tagBusyId: null,
    };
  },
  mounted() {
    this.loadPlaceTags();
  },
  methods: {
    ...mapActions({
      applyTagAction: "inbox/applyTag",
      removeTagAction: "inbox/removeTag",
    }),
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    async loadPlaceTags() {
      try {
        const response = await this.$http(keviApi.getOptions("forms-tags"));
        this.placeTags = (response.body && response.body.tags) || [];
      } catch (error) {
        this.placeTags = [];
      }
    },
    suggestionsFor(answer) {
      const formId = answer && answer.form_id;
      if (!formId) return this.placeTags;
      return this.placeTags.filter(
        (tag) => !tag.forms_id || String(tag.forms_id) === String(formId),
      );
    },
    patchAnswerTags(answerId, tags) {
      const report = this.insights && this.insights.forms_report;
      if (!report) return;
      const lists = [report.answers, report.last_forms_sent].filter(Array.isArray);
      lists.forEach((list) => {
        const item = list.find(
          (answer) => String(answer.answer_id) === String(answerId),
        );
        if (item) {
          this.$set(item, "tags", tags || []);
        }
      });
    },
    applyListTag(answer, payload) {
      this.tagBusyId = answer.answer_id;
      this.applyTagAction({
        messageId: answer.answer_id,
        ...payload,
      })
        .then((body) => {
          if (body && body.tags) {
            this.patchAnswerTags(answer.answer_id, body.tags);
          }
          if (body && body.tag) {
            const exists = this.placeTags.some(
              (tag) => String(tag.id) === String(body.tag.id),
            );
            if (!exists) {
              this.placeTags = this.placeTags.concat([
                { ...body.tag, forms_id: answer.form_id },
              ]);
            }
          }
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("inbox-message-notification_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    removeListTag(answer, tag) {
      this.tagBusyId = answer.answer_id;
      this.removeTagAction({
        messageId: answer.answer_id,
        tagId: tag.id,
      })
        .then((body) => {
          if (body && body.tags) {
            this.patchAnswerTags(answer.answer_id, body.tags);
          }
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("inbox-message-notification_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    patchTagAcrossAnswers(tag) {
      if (!tag || !tag.id) return;
      this.placeTags = (this.placeTags || []).map((item) =>
        String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
      );
      const report = this.insights && this.insights.forms_report;
      if (!report) return;
      const lists = [report.answers, report.last_forms_sent].filter(Array.isArray);
      lists.forEach((list) => {
        list.forEach((answer) => {
          const tags = answer.tags || [];
          if (!tags.some((item) => String(item.id) === String(tag.id))) return;
          this.$set(
            answer,
            "tags",
            tags.map((item) =>
              String(item.id) === String(tag.id) ? { ...item, ...tag } : item,
            ),
          );
        });
      });
    },
    removeTagAcrossAnswers(tagId) {
      this.placeTags = (this.placeTags || []).filter(
        (item) => String(item.id) !== String(tagId),
      );
      const report = this.insights && this.insights.forms_report;
      if (!report) return;
      const lists = [report.answers, report.last_forms_sent].filter(Array.isArray);
      lists.forEach((list) => {
        list.forEach((answer) => {
          const tags = answer.tags || [];
          if (!tags.some((item) => String(item.id) === String(tagId))) return;
          this.$set(
            answer,
            "tags",
            tags.filter((item) => String(item.id) !== String(tagId)),
          );
        });
      });
    },
    updateTagDefinition(answer, payload) {
      if (!payload || !payload.id || !answer || !answer.form_id) return;
      this.tagBusyId = answer.answer_id;
      const options = keviApi.getOptions(
        "forms/" + answer.form_id + "/tags",
        "POST",
        {
          id: payload.id,
          name: payload.name,
          color: payload.color,
        },
      );
      this.$http(options)
        .then((response) => {
          const tag = (response.body && response.body.tag) || payload;
          this.patchTagAcrossAnswers({
            ...tag,
            forms_id: answer.form_id,
          });
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("inbox-message-notification_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    deleteTagDefinition(answer, tag) {
      if (!tag || !tag.id || !answer || !answer.form_id) return;
      this.tagBusyId = answer.answer_id;
      const options = keviApi.getOptions(
        "forms/" + answer.form_id + "/tags/" + tag.id,
        "DELETE",
      );
      this.$http(options)
        .then(() => {
          this.removeTagAcrossAnswers(tag.id);
        })
        .catch(() => {
          this.$store.dispatch(
            "notification/error",
            this.t("inbox-message-notification_tag_error"),
          );
        })
        .finally(() => {
          this.tagBusyId = null;
        });
    },
    onDrawerTagsChanged() {
      if (typeof this.loadFormsReport === "function") {
        this.loadFormsReport(this.preview.forms);
      }
    },
    onAnswerDuplicated() {
      if (typeof this.loadFormsReport === "function") {
        this.loadFormsReport(this.preview.forms);
      }
    },
    handleDrawerHide() {
      this.activeMessage = null;
      if (typeof this.loadFormsReport === "function") {
        this.loadFormsReport(this.preview.forms);
      }
    },
    getIconForSummary(item) {
      if (item.type === "emotion") {
        return this.getEmotionIcon(item.value);
      }
      const icons = {
        comment: "mdi mdi-comment-text-outline type-icon",
        boolean: "mdi mdi-check-circle-outline type-icon",
        selectbox: "mdi mdi-format-list-bulleted type-icon",
        input: "mdi mdi-format-text type-icon",
        nps: "mdi mdi-gauge type-icon",
        ces: "mdi mdi-arm-flex-outline type-icon",
        date: "mdi mdi-calendar-outline type-icon",
        file: "mdi mdi-paperclip type-icon",
      };
      return icons[item.type] || "mdi mdi-circle-small type-icon";
    },
    getEmotionIcon(value) {
      const score = Math.round(parseFloat(value) || 0);
      if (score >= 5) return "mdi mdi-emoticon-excited-outline type-icon";
      if (score >= 4) return "mdi mdi-emoticon-happy-outline type-icon";
      if (score >= 3) return "mdi mdi-emoticon-neutral-outline type-icon";
      if (score >= 2) return "mdi mdi-emoticon-sad-outline type-icon";
      return "mdi mdi-emoticon-cry-outline type-icon";
    },
    visibleTypeSummary(answer) {
      return (answer.type_summary || []).filter((item) => {
        if (item.count != null) {
          return parseInt(item.count, 10) > 0;
        }
        return item.value != null;
      });
    },
    formatTypeValue(item) {
      if (item.type === "boolean") {
        return parseInt(item.value, 10) > 0
          ? this.t("inbox-message-bool_yes")
          : this.t("inbox-message-bool_no");
      }
      return item.value;
    },
    typeToneClass(item) {
      return `tone-${this.typeTone(item)}`;
    },
    typeTone(item) {
      if (item.value == null) return "muted";
      const value = parseFloat(item.value);
      if (item.type === "nps") {
        if (value > 8) return "good";
        if (value > 6) return "neutral";
        return "bad";
      }
      if (item.type === "ces") {
        if (value > 4) return "good";
        if (value === 4) return "neutral";
        return "bad";
      }
      if (item.type === "emotion") {
        if (value >= 4) return "good";
        if (value >= 3) return "neutral";
        return "bad";
      }
      if (item.type === "boolean") {
        return value > 0 ? "good" : "bad";
      }
      return "muted";
    },
    typeSummaryTitle(item) {
      const typeLabel = this.t("inbox-type_" + item.type);
      if (item.type === "nps" && item.value != null) {
        const value = parseFloat(item.value);
        const band =
          value > 8
            ? this.t("inbox-message-nps_promoter")
            : value > 6
              ? this.t("inbox-message-nps_neutral")
              : this.t("inbox-message-nps_detractor");
        return `${typeLabel}: ${item.value} (${band})`;
      }
      if (item.type === "ces" && item.value != null) {
        const value = parseFloat(item.value);
        const band =
          value > 4
            ? this.t("inbox-message-ces_easy")
            : value === 4
              ? this.t("inbox-message-ces_medium")
              : this.t("inbox-message-ces_hard");
        return `${typeLabel}: ${item.value} (${band})`;
      }
      if (item.value != null) {
        return `${typeLabel}: ${this.formatTypeValue(item)}`;
      }
      if (item.count) {
        return `${typeLabel}: ${item.count}`;
      }
      return typeLabel;
    },
    getPredictionState(dateStr) {
      if (!dateStr) return { color: 'default', label: '-' };

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const target = new Date(dateStr);
      target.setHours(0, 0, 0, 0);

      const diffTime = target - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return { color: 'default', label: this.t('days_ago', [Math.abs(diffDays)]) };
      }

      if (diffDays === 0) {
        return { color: 'error', label: this.t('today') };
      }

      if (diffDays === 1) {
        return { color: 'warning', label: this.t('tomorrow') };
      }

      return { color: 'success', label: this.t('in_x_days', [diffDays]) };
    },
    changePeriod(value, callback) {
      callback(value);
    },
    openForm(id) {
      if (id == null || id === "") return;
      // Keep as string — snowflake IDs exceed JS safe integer range.
      this.activeMessage = String(id);
      this.$nextTick(() => {
        if (this.$refs.messageDrawer) {
          this.$refs.messageDrawer.open();
        }
      });
    },
    async togglePreview(prop, callback) {
      const preview = this.preview[prop] === "last" ? "all" : "last";

      await callback(preview);

      this.preview = { ...this.preview, [prop]: preview };
    },
    parseTransactionDescription(description) {
      if (description === "added") {
        return this.t("clients-insights-added");
      }
      if (description === "removed") {
        return this.t("clients-insights-removed");
      }
      return description;
    },
    formatDateLabel(dateStr) {
      const date = new Date(dateStr);

      const formatted = new Intl.DateTimeFormat("pt-BR", {
        month: "short",
        year: "numeric"
      })
        .format(date)
        .replace('.', '');

      const result = formatted.charAt(0).toUpperCase() + formatted.slice(1);
      return result;
    },
    getCouponStatus(coupon) {
      if (coupon.used != 0) {
        return {
          label: this.t('clients-insights-used'),
          color: 'default',
          variant: 'tonal'
        };
      }
      const now = new Date();

      const expirationDate = new Date(coupon.expiration_date);
      if (expirationDate < now) {
        return {
          label: this.t('clients-insights-expired'),
          color: 'error',
          variant: 'tonal'
        };
      }

      const validFrom = new Date(coupon.valid_from);

      if (now < validFrom) {
        return {
          label: this.t('clients-detail-status_scheduled'),
          color: 'warning',
          variant: 'tonal'
        };
      }

      return {
        label: this.t('clients-insights-available'),
        color: 'success',
        variant: 'default'
      };
    },
    formatNumber(number) {
      if (isNaN(number)) {
        return (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return parseFloat(number).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    scrollToElement(id) {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    getTime(date) {
      const hora = date.getHours();      // 0–23
      const minuto = date.getMinutes();  // 0–59

      return `${hora}:${minuto}`;
    },
    async handleSendMessage() {
      if (!this.question) return;

      const question = this.question;
      this.question = "";

      const userId = `user-${Date.now()}`;

      // Mensagem do usuário
      this.insights.messages.push({
        id: userId,
        aiMessage: false,
        content: question,
        date: new Date()
      });

      this.$nextTick(() => {
        this.$refs.aiScrollbar?.scrollToBottom();
      });

      const aiId = await this.sendAiMessage(question);

      this.$refs.aiScrollbar?.scrollToElementId(aiId, 10);
    },
    conversionChart({ total, converted }) {
      const isEmpty = total === 0;

      const remaining = isEmpty ? 1 : Math.max(0, total - converted);

      const actualConverted = isEmpty ? 0 : converted;
      const data = {
        datasets: [
          {
            backgroundColor: ["#E5E7EB", "#2970FF"],
            hoverBackgroundColor: ["#E5E7EB", "#2970FF"],
            borderWidth: 0,
            data: [remaining, actualConverted]
          }
        ],
        labels: [this.t("clients-insights-pending_plural"), this.t("clients-insights-completed_plural")]
      };

      return data;
    }
  },
  computed: {
    purchases_report() {
      return this.insights.purchases_report || {
        first_purchase: null,
        last_purchase: null,
        next_order_prediction: null,
        most_purchased_item: null,
        cumulative_spend: { total_value: 0, chart_data: null },
        total_purchases: { total_quantity: 0, chart_data: null },
      };
    },
    predictions_report() {
      return this.insights.predictions_report;
    },
    notifications_report() {
      const report = this.insights.notifications_report || {};

      const contacts = Array.isArray(report.contacts_by_period)
        ? report.contacts_by_period
        : [];

      const milestones = Array.isArray(report.contact_milestones)
        ? report.contact_milestones
        : [];

      const getQuantity = (method) =>
        Number(contacts.find(x => x.method === method)?.quantity || 0);

      const getMilestone = (method) =>
        milestones.find(x => x.method === method) || {};

      return {
        ...report, contacts: {
          email: getQuantity('email'),
          sms: getQuantity('sms'),
          whatsapp: getQuantity('whatsapp'),
          rcs: getQuantity('rcs')
        }, milestones: {
          email: getMilestone('email'),
          sms: getMilestone('sms'),
          whatsapp: getMilestone('whatsapp'),
          rcs: getMilestone('rcs')
        }
      };
    },
    rewards_report() {
      return this.insights.rewards_report || {
        available_points: 0,
        total_accumulated: 0,
        total_used: 0,
        last_transactions: [],
      };
    },
    forms_report() {
      const report = this.insights.forms_report || {};
      const answers = Array.isArray(report.answers)
        ? report.answers
        : Array.isArray(report.last_forms_sent)
          ? report.last_forms_sent
          : [];

      return {
        ...report,
        answers,
        total_answers: report.total_answers ?? answers.length,
      };
    },
    tasks_report() {
      return this.insights.tasks_report || { last_tasks: [] };
    },
    coupons_report() {
      return this.insights.coupons_report || { last_generated_coupons: [] };
    },
    notificationsChartData() {
      const report = this.insights.notifications_report || {};
      const contacts = Array.isArray(report.contacts_by_period) ? report.contacts_by_period : [];

      // Lógica auxiliar local para extrair os números
      const getQuantity = (method) =>
        Number(contacts.find(x => x.method === method)?.quantity || 0);

      const email = getQuantity('email');
      const sms = getQuantity('sms');
      const whatsapp = getQuantity('whatsapp');
      const rcs = getQuantity('rcs');

      return {
        labels: [this.t('clients-columns-email'), this.t('clients-detail-sms'), this.t('clients-detail-whatsapp'), this.t('rcs')],
        datasets: [
          {
            backgroundColor: ["#FDA712", "#2970FF", "#17B26A", "#5B4DFF"],
            data: [email, sms, whatsapp, rcs]
          }
        ]
      };
    },
    calculatePoints() {
      return this.changePoint.active ?
        this.rewards_report.available_points + this.changePoint.amount :
        this.rewards_report.available_points - this.changePoint.amount;
    },
    calculateAmount() {
      return this.changePoint.active ? this.changePoint.amount : this.changePoint.amount * -1;
    },
    cumulative_spend_chart() {
      if (!this.purchases_report.cumulative_spend.chart_data) return { datasets: [], labels: [], tooltips: [] };

      const rawData = this.purchases_report.cumulative_spend.chart_data;
      const values = rawData.map(d => parseFloat(d.value));
      return {
        datasets: values,
        labels: rawData.map(d => this.formatDateLabel(d.period)),
        tooltips: rawData.map(d => {
          const label = d.label;
          const value = parseFloat(d.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
          return `${label}: ${value}`;
        })
      };
    },

    total_purchases_chart() {
      if (!this.purchases_report.total_purchases.chart_data) return { datasets: [], labels: [], tooltips: [] };

      const rawData = this.purchases_report.total_purchases.chart_data;
      const values = rawData.map(d => parseFloat(d.quantity));

      return {
        datasets: values,
        labels: rawData.map(d => this.formatDateLabel(d.period)),
        tooltips: rawData.map(d => {
          const label = d.label;
          const value = d.quantity
          return `${label}: ${value}`;
        })
      }
    },
    place() {
      return this.$store.state.place;
    },
    hasMessages() {
      return this.insights?.messages && this.insights.messages.length > 0;
    },
  }
};
</script>

<style lang="less">
.client-insights-root {
  display: contents;
}

.client-answers-list {
  display: flex;
  flex-direction: column;
  margin: 0 -12px -8px;
}

.client-answer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #F3F4F6;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  .form-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
    flex: 1;
  }

  .form-name {
    font-size: 13px;
    color: #6C737F;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex-shrink: 1;
  }

  .type-summary {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
    flex-shrink: 0;
  }

  .type-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    color: #9DA4AE;

    .type-icon {
      font-size: 16px;
      line-height: 1;
      color: currentColor;
      display: inline-flex;
      width: 16px;
      height: 16px;
    }

    &.tone-good { color: #0B9055; }
    &.tone-neutral { color: #F79009; }
    &.tone-bad { color: #F04438; }
    &.tone-muted { color: #6C737F; }
  }

  .message-tags-cell {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
  }

  .date {
    font-size: 12px;
    color: #9DA4AE;
    white-space: nowrap;
    text-align: right;
    flex-shrink: 0;
  }

  &:hover {
    background: #F9FAFB;
  }

  &.selected {
    background: #F0F7F4;
    box-shadow: inset 3px 0 0 var(--primary);
  }
}

.kv-ai-chat-limit {
  height: 160px;
  gap: 16px;
  transition: height 0.3s ease-in-out;
}

.kv-ai-chat-limit.kv-ai-chat-expanded {
  height: 550px;
}

.rewards-container {
  max-height: 350px;
}

.rewards-table {
  width: 100%;
  border-collapse: collapse;
}

/* Alinhamento das colunas */
.rewards-table th:first-child,
.rewards-table td:first-child {
  text-align: left;
}

.rewards-table th:last-child,
.rewards-table td:last-child {
  text-align: right;
}

/* Linha abaixo do thead */
.rewards-table thead tr {
  border-bottom: 1px solid #E5E7EB;
}

/* Linha entre os tr */
.rewards-table tbody tr {
  border-bottom: 1px solid #E5E7EB;
}

/* Remove a linha do último item */
.rewards-table tbody tr:last-child {
  border-bottom: none;
}

/* Espaçamento */
.rewards-table th,
.rewards-table td {
  padding: 16px 0;
}

.insights {
  .line {
    height: 1px;
    width: 100%;
    background-color: #E5E7EB;
  }

  .notifications {
    display: grid;
    grid-template-columns: auto auto auto;

    @media (max-width: 1160px) {
      grid-template-columns: 1fr;
    }
  }

  .grid-items {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }
}

.kv-ai-chat {
  display: grid;
  grid-template-rows: 1fr auto;
  align-items: center;
  min-height: 0;

  &:has(.kv-ai-messages) {
    align-items: start;

    .kv-ai-messages {
      display: flex;
      flex-direction: column;
      gap: 50px;
    }
  }

  .chat-message.user-message .message-body {
    background-color: #00542E;
    color: #F1FCF3;
    border-radius: 16px 16px 0px 16px;
    margin-left: auto;
  }

  .chat-message.ai-message .message-body {
    background-color: #F9FAFB;
    color: #384250;
    border-radius: 16px 16px 16px 0px;
    justify-self: flex-start;
    position: relative;
    transition: all 0.3s ease-in-out;

    &.message-loading {
      width: 80px;
      padding: 10px;
    }

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: inherit;

      background: linear-gradient(135deg,
          #F49E8A 0%,
          #E83E76 30%,
          #936CAD 65%,
          #2DBBDD 100%);

      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
    }
  }

  .chat-message .message-body {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 6px;
    width: 60%;
    font-size: 14px;
    overflow: hidden;

    .date {
      font-weight: 600;
      font-size: 10px;
    }
  }

  .kv-ai-placeholder {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: center;
    text-align: center;
    justify-content: center;
    padding: 8px 0;
  }
}

.ai-message {
  flex: 1;
}

.show-on-hover {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;

  @media (max-width: 768px) {
    opacity: 1;
    pointer-events: auto;
  }
}

/* Quando passar o mouse no card */
.form-item:hover .show-on-hover {
  opacity: 1;
  pointer-events: auto;
}

.coupon-item {
  border-style: dashed;
  border-width: 2px;
}

.circle-bg {
  display: flex;
  align-items: center;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    background-color: #FDA712;
    border-radius: 50%;
    display: inline-block;
    margin-right: 4px;
  }


  &.sms::before {
    background-color: #2970FF;
  }

  &.rcs::before {
    background-color: #5B4DFF;
  }

  &.whatsapp::before {
    background-color: #17B26A;
  }
}

@media (max-width: 768px) {
  .mobile-column-layout {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 8px;

    >.kv-typography,
    >.flex-1 {
      width: 100%;
      margin-bottom: 4px;
      order: 2;
    }

    .mobile-actions-row {
      width: 100%;
      justify-content: space-between;
      align-items: flex-start !important;

      .kv-button,
      .kv-copy-button {
        flex-shrink: 0;
        order: 2;
      }
    }
  }

  .coupon-layout {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 12px;

    .coupon-actions {
      order: -1;
      width: 100%;
      justify-content: space-between;
      margin-bottom: 4px;
      align-items: flex-start !important;
    }

    .coupon-info {
      width: 100%;
    }
  }
}

.dots-loader {
  width: 60px;
  aspect-ratio: 2;

  background: linear-gradient(135deg,
      #F49E8A 0%,
      #E83E76 30%,
      #936CAD 65%,
      #2DBBDD 100%);

  --_m: no-repeat radial-gradient(circle closest-side,
      #000 82%,
      transparent);

  -webkit-mask:
    var(--_m) 0% 50%,
    var(--_m) 50% 50%,
    var(--_m) 100% 50%;
  mask:
    var(--_m) 0% 50%,
    var(--_m) 50% 50%,
    var(--_m) 100% 50%;

  -webkit-mask-size: calc(100% / 3) 38%;
  mask-size: calc(100% / 3) 38%;

  animation: l3 1s infinite linear;
}

@keyframes l3 {
  20% {
    -webkit-mask-position: 0% 0%, 50% 50%, 100% 50%;
  }

  40% {
    -webkit-mask-position: 0% 100%, 50% 0%, 100% 50%;
  }

  60% {
    -webkit-mask-position: 0% 50%, 50% 100%, 100% 0%;
  }

  80% {
    -webkit-mask-position: 0% 50%, 50% 50%, 100% 100%;
  }

  /* Firefox */
  20% {
    mask-position: 0% 0%, 50% 50%, 100% 50%;
  }

  40% {
    mask-position: 0% 100%, 50% 0%, 100% 50%;
  }

  60% {
    mask-position: 0% 50%, 50% 100%, 100% 0%;
  }

  80% {
    mask-position: 0% 50%, 50% 50%, 100% 100%;
  }
}
</style>